// src/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  useEffect(() => {
    const savedEmail = localStorage.getItem('sqp_remembered_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (rememberMe) {
      localStorage.setItem('sqp_remembered_email', email);
    } else {
      localStorage.removeItem('sqp_remembered_email');
    }
    const { error } = await signIn(email, password);
    if (error) {
      setError('Email ou mot de passe incorrect.');
      setLoading(false);
      return;
    }
    navigate('/');
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotError('');
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: 'https://devis.takoussane.com/change-password',
    });
if (error) {
      setForgotError('Erreur : ' + error.message);
    } else {
      sessionStorage.setItem('sqp_reset_mode', '1');
      setForgotSuccess(true);
    }
    setForgotLoading(false);
  };

  const inputClass = "w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400";
  const btnPrimary = (disabled) => `w-full py-3 rounded-lg text-white font-semibold transition-colors ${disabled ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8"
      >
        {/* Logo + titre */}
        <div className="text-center mb-8">
          <img
            src="/favicon.png"
            alt="TAKOUSSANE ENERGY"
            className="h-16 mx-auto mb-4 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-2xl font-bold text-gray-800">
            SolarQuotePro
            <sup style={{ fontSize: '10px', fontWeight: 400, color: '#9ca3af', verticalAlign: 'super', marginLeft: '1px' }}>TM</sup>
          </h1>
          <p className="text-gray-500 text-sm mt-1">TAKOUSSANE ENERGY — Espace Commercial</p>
        </div>

        <AnimatePresence mode="wait">

          {/* ── Formulaire connexion ── */}
          {!showForgot && (
            <motion.div key="login"
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}>
              <form onSubmit={handleSubmit} className="space-y-5">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email" name="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className={inputClass} required autoFocus={!email}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                  <input
                    type="password" name="password" autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={inputClass} required autoFocus={!!email}
                  />
                </div>

                {/* Se souvenir de moi + Mot de passe oublié — même ligne, même baseline */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '20px' }}>
                  <label htmlFor="rememberMe" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox" id="rememberMe"
                      checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                      style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer', flexShrink: 0, display: 'block', position: 'relative', top: '7px' }}
                    />
                    <span style={{ fontSize: '14px', color: '#4b5563', userSelect: 'none', lineHeight: '20px' }}>
                      Se souvenir de moi
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotError(''); setForgotSuccess(false); }}
                    style={{ fontSize: '14px', color: '#2563eb', background: 'none', border: 'none',
                      cursor: 'pointer', padding: 0, lineHeight: '20px' }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                  >
                    Mot de passe oublié ?
                  </button>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-red-700 text-sm">
                    ⚠️ {error}
                  </div>
                )}

                <button type="submit" disabled={loading} className={btnPrimary(loading)}>
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>

              </form>
            </motion.div>
          )}

          {/* ── Formulaire mot de passe oublié ── */}
          {showForgot && (
            <motion.div key="forgot"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}>

              {forgotSuccess ? (
                <div className="text-center space-y-4">
                  <div style={{ fontSize: '48px' }}>📧</div>
                  <div className="text-green-600 font-semibold text-lg">Email envoyé !</div>
<p className="text-gray-500 text-sm">
  Si l'adresse <strong>{forgotEmail}</strong> est associée à un compte,
  vous recevrez un lien de réinitialisation. Vérifiez votre boîte mail (et les spams).
</p>
                  <button onClick={() => { setShowForgot(false); setForgotSuccess(false); }} className={btnPrimary(false)}>
                    Retour à la connexion
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="text-center mb-2">
                    <div className="text-lg font-bold text-gray-800 mb-1">Mot de passe oublié</div>
                    <p className="text-sm text-gray-500">
                      Entrez votre adresse email — nous vous enverrons un lien de réinitialisation.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email" value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className={inputClass} required autoFocus
                    />
                  </div>

                  {forgotError && (
                    <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-red-700 text-sm">
                      ⚠️ {forgotError}
                    </div>
                  )}

                  <button type="submit" disabled={forgotLoading || !forgotEmail.trim()} className={btnPrimary(forgotLoading || !forgotEmail.trim())}>
                    {forgotLoading ? 'Envoi...' : 'Envoyer le lien'}
                  </button>

                  <button type="button" onClick={() => setShowForgot(false)}
                    className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                    ← Retour à la connexion
                  </button>
                </form>
              )}
            </motion.div>
          )}

        </AnimatePresence>

        <p className="text-center text-xs text-gray-400 mt-6">
          TAKOUSSANE ENERGY © {new Date().getFullYear()}
        </p>
      </motion.div>
    </div>
  );
};

export default LoginPage;
